/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetStripeSubscriptionRequest,
  GetStripeSubscriptionSecurity,
} from "../models/operations/getstripesubscription.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGetStripeSubscriptionQuery,
  GetStripeSubscriptionQueryData,
  prefetchGetStripeSubscription,
  queryKeyGetStripeSubscription,
} from "./getStripeSubscription.core.js";
export {
  buildGetStripeSubscriptionQuery,
  type GetStripeSubscriptionQueryData,
  prefetchGetStripeSubscription,
  queryKeyGetStripeSubscription,
};

export type GetStripeSubscriptionQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getStripeSubscription usage
 *
 * @remarks
 * Get the live lifecycle state of the organization's Stripe PAYG subscription
 */
export function useGetStripeSubscription(
  request?: GetStripeSubscriptionRequest | undefined,
  security?: GetStripeSubscriptionSecurity | undefined,
  options?: QueryHookOptions<
    GetStripeSubscriptionQueryData,
    GetStripeSubscriptionQueryError
  >,
): UseQueryResult<
  GetStripeSubscriptionQueryData,
  GetStripeSubscriptionQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildGetStripeSubscriptionQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getStripeSubscription usage
 *
 * @remarks
 * Get the live lifecycle state of the organization's Stripe PAYG subscription
 */
export function useGetStripeSubscriptionSuspense(
  request?: GetStripeSubscriptionRequest | undefined,
  security?: GetStripeSubscriptionSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GetStripeSubscriptionQueryData,
    GetStripeSubscriptionQueryError
  >,
): UseSuspenseQueryResult<
  GetStripeSubscriptionQueryData,
  GetStripeSubscriptionQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGetStripeSubscriptionQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGetStripeSubscriptionData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: GetStripeSubscriptionQueryData,
): GetStripeSubscriptionQueryData | undefined {
  const key = queryKeyGetStripeSubscription(...queryKeyBase);

  return client.setQueryData<GetStripeSubscriptionQueryData>(key, data);
}

export function invalidateGetStripeSubscription(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "usage",
      "getStripeSubscription",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllGetStripeSubscription(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "usage", "getStripeSubscription"],
  });
}
